/**
 * Time Entries Database Helpers
 *
 * Firestore CRUD operations for time entries on work orders.
 *
 * Collection path: teams/{teamId}/workOrders/{workOrderId}/timeEntries/{timeEntryId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  type CollectionReference,
  type DocumentReference,
} from 'firebase/firestore';
import { addDoc } from '@/lib/firestore/writes';
import { db } from '@/lib/firebase';
import type {
  TimeEntry,
  TimeEntryDocument,
  ManualTimeEntryInput,
  TimeEntryUpdateInput,
  TimeEntryFromTimerInput,
} from '../types/timeEntry';
import {
  timestampToIso,
  computeDurationMinutes as calcDuration,
  computeTotalCost as calcCost,
} from '../types/timeEntry';

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as Partial<T>;
}

/**
 * Get collection reference for time entries within a work order
 */
export function timeEntriesCol(
  teamId: string,
  workOrderId: string
): CollectionReference<TimeEntryDocument> {
  return collection(
    db,
    'teams',
    teamId,
    'workOrders',
    workOrderId,
    'timeEntries'
  ) as CollectionReference<TimeEntryDocument>;
}

/**
 * Get document reference for a specific time entry
 */
export function timeEntryDoc(
  teamId: string,
  workOrderId: string,
  timeEntryId: string
): DocumentReference<TimeEntryDocument> {
  return doc(
    db,
    'teams',
    teamId,
    'workOrders',
    workOrderId,
    'timeEntries',
    timeEntryId
  ) as DocumentReference<TimeEntryDocument>;
}

/**
 * Convert Firestore document to TimeEntry
 */
function convertTimeEntryDoc(data: TimeEntryDocument, id: string): TimeEntry {
  return {
    ...data,
    id,
    startedAt: timestampToIso(data.startedAt),
    endedAt: timestampToIso(data.endedAt),
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
  };
}

/**
 * List all time entries for a work order
 * Ordered by startedAt descending (most recent first)
 */
export async function listTimeEntries(teamId: string, workOrderId: string): Promise<TimeEntry[]> {
  const col = timeEntriesCol(teamId, workOrderId);
  const q = query(col, orderBy('startedAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => convertTimeEntryDoc(doc.data(), doc.id));
}

/**
 * Get a single time entry by ID
 */
export async function getTimeEntry(
  teamId: string,
  workOrderId: string,
  timeEntryId: string
): Promise<TimeEntry | null> {
  const docRef = timeEntryDoc(teamId, workOrderId, timeEntryId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return convertTimeEntryDoc(docSnap.data(), docSnap.id);
}

/**
 * Create a time entry from a stopped timer
 * Used internally by stopWorkOrderTimer
 */
export async function createTimeEntryFromTimer(
  teamId: string,
  workOrderId: string,
  input: TimeEntryFromTimerInput,
  userId: string
): Promise<string> {
  const durationMinutes = calcDuration(input.startedAt, input.endedAt);
  const totalCost = calcCost(durationMinutes, input.hourlyCostSnapshot);
  const totalSell = input.hourlySellSnapshot
    ? calcCost(durationMinutes, input.hourlySellSnapshot)
    : undefined;

  const col = timeEntriesCol(teamId, workOrderId);
  const data = stripUndefined({
    workOrderId,
    mechanicId: input.mechanicId,
    mechanicName: input.mechanicName,
    startedAt: input.startedAt,
    endedAt: input.endedAt,
    durationMinutes,
    hourlyCostSnapshot: input.hourlyCostSnapshot,
    hourlySellSnapshot: input.hourlySellSnapshot,
    totalCost,
    totalSell,
    notes: input.notes,
    source: 'TIMER' as const,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
  });

  const ref = await addDoc(col, data as any);
  await updateDoc(ref, { id: ref.id });
  return ref.id;
}

/**
 * Create a manual time entry
 */
export async function createManualTimeEntry(
  teamId: string,
  workOrderId: string,
  input: ManualTimeEntryInput,
  userId: string
): Promise<string> {
  // Parse dates
  const startedAt = Timestamp.fromDate(new Date(input.startedAt));
  const endedAt = Timestamp.fromDate(new Date(input.endedAt));

  // Compute duration (use override if provided)
  const durationMinutes = input.durationMinutes ?? calcDuration(startedAt, endedAt);
  const totalCost = calcCost(durationMinutes, input.hourlyCostSnapshot);
  const totalSell = input.hourlySellSnapshot
    ? calcCost(durationMinutes, input.hourlySellSnapshot)
    : undefined;

  const col = timeEntriesCol(teamId, workOrderId);
  const data = stripUndefined({
    workOrderId,
    mechanicId: input.mechanicId,
    mechanicName: input.mechanicName,
    startedAt,
    endedAt,
    durationMinutes,
    hourlyCostSnapshot: input.hourlyCostSnapshot,
    hourlySellSnapshot: input.hourlySellSnapshot,
    totalCost,
    totalSell,
    notes: input.notes,
    source: 'MANUAL' as const,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
  });

  const ref = await addDoc(col, data as any);
  await updateDoc(ref, { id: ref.id });
  return ref.id;
}

/**
 * Update a time entry
 */
export async function updateTimeEntry(
  teamId: string,
  workOrderId: string,
  timeEntryId: string,
  input: TimeEntryUpdateInput
): Promise<void> {
  const docRef = timeEntryDoc(teamId, workOrderId, timeEntryId);

  // Build update data
  const updateData: Record<string, any> = {
    updatedAt: serverTimestamp(),
  };

  if (input.startedAt !== undefined) {
    updateData.startedAt = Timestamp.fromDate(new Date(input.startedAt));
  }
  if (input.endedAt !== undefined) {
    updateData.endedAt = Timestamp.fromDate(new Date(input.endedAt));
  }
  if (input.durationMinutes !== undefined) {
    updateData.durationMinutes = input.durationMinutes;
  }
  if (input.hourlyCostSnapshot !== undefined) {
    updateData.hourlyCostSnapshot = input.hourlyCostSnapshot;
  }
  if (input.hourlySellSnapshot !== undefined) {
    updateData.hourlySellSnapshot = input.hourlySellSnapshot;
  }
  if (input.notes !== undefined) {
    updateData.notes = input.notes;
  }

  // Recompute totals if duration or rates changed
  if (
    input.durationMinutes !== undefined ||
    input.hourlyCostSnapshot !== undefined ||
    input.hourlySellSnapshot !== undefined ||
    input.startedAt !== undefined ||
    input.endedAt !== undefined
  ) {
    // Get current doc to compute new totals
    const currentDoc = await getDoc(docRef);
    if (currentDoc.exists()) {
      const current = currentDoc.data();
      const startedAt = updateData.startedAt ?? current.startedAt;
      const endedAt = updateData.endedAt ?? current.endedAt;
      const durationMinutes = updateData.durationMinutes ?? calcDuration(startedAt, endedAt);
      const hourlyCost = updateData.hourlyCostSnapshot ?? current.hourlyCostSnapshot;
      const hourlySell = updateData.hourlySellSnapshot ?? current.hourlySellSnapshot;

      updateData.durationMinutes = durationMinutes;
      updateData.totalCost = calcCost(durationMinutes, hourlyCost);
      if (hourlySell) {
        updateData.totalSell = calcCost(durationMinutes, hourlySell);
      }
    }
  }

  await updateDoc(docRef, updateData);
}

/**
 * Delete a time entry
 */
export async function deleteTimeEntry(
  teamId: string,
  workOrderId: string,
  timeEntryId: string
): Promise<void> {
  const docRef = timeEntryDoc(teamId, workOrderId, timeEntryId);
  await deleteDoc(docRef);
}

/**
 * Get total time tracked for a work order (in minutes)
 */
export async function getTotalTimeForWorkOrder(
  teamId: string,
  workOrderId: string
): Promise<{ totalMinutes: number; totalCost: number }> {
  const entries = await listTimeEntries(teamId, workOrderId);
  const totalMinutes = entries.reduce((sum, e) => sum + e.durationMinutes, 0);
  const totalCost = entries.reduce((sum, e) => sum + e.totalCost, 0);
  return { totalMinutes, totalCost };
}
